import { useState } from "react";

import { isNil } from "lodash";
import { usePostHog } from "posthog-js/react";

import { useAPI, useShipmentPageContext } from "hooks";

import { CounterQuoteInteraction } from "./CouterQuoteInteraction";
import { ClientAppEvent } from "../../../../../../constants";

import type { CounterQuoteFormState } from "./types";

const initialState: CounterQuoteFormState = {
  currency: "",
  price: null,
  notes: "",
};

interface Props {
  quoteId: number;
  onClose: VoidFunction;
}

export function CounterQuoteInteractionContainer(props: Props) {
  const { onClose, quoteId } = props;
  const [formState, setFormState] =
    useState<CounterQuoteFormState>(initialState);
  const { api } = useAPI();
  const { shipment, onRefresh } = useShipmentPageContext();
  const posthog = usePostHog();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const onConfirmAction = () => {
    setError(null);
    setLoading(true);

    api<unknown>("post", `/quotes/${quoteId}/counter`, formState)
      .then(() => {
        posthog?.capture(ClientAppEvent.QUOTE_COUNTERED, {
          quote_id: quoteId,
          shipment_id: shipment?.info?.id,
        });
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      })
      .catch((e) => {
        setError(e?.response?.data?.message ?? "Failed to counter quote");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onValueChanged = <T,>(
    key: keyof CounterQuoteFormState,
    value: T,
  ): void => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (key === "price") {
      setError(null);
    }
  };

  return (
    <CounterQuoteInteraction
      onClose={onClose}
      onConfirmAction={onConfirmAction}
      error={error}
      loading={loading}
      onInputChange={onValueChanged}
      disableSubmit={
        formState.currency === "" ||
        isNil(formState.price) ||
        formState.price <= 0
      }
      formState={formState}
    />
  );
}
